import { parse } from 'date-fns'

import type { DataPoint } from '@/components/DateTotalsGraph/types'
import createAgentApiService from '@/lib/agentApiServices/service'
import type { TransformedDateTotalActivitiesData } from '@/lib/apollo/query/getTotalActivitiesByDateForUser'
import { downloadBlob } from '@/utils/blob'
import type { DateRange } from '@/utils/date'
import { formatDate, formatDateISO, yyyyMMddHHmm } from '@/utils/date'
import type { TimeInterval } from '@/utils/dateRange'
import { getDatesBetweenRange } from '@/utils/dateRange'

export const rowsPerPage = 10

type DateRangeFilters = {
  startDate: string
  endDate: string
}

export const toDateFilterPayload = (dateRange: DateRange): DateRangeFilters => ({
  startDate: formatDateISO(dateRange.startDate),
  endDate: formatDateISO(dateRange.endDate),
})

type KeyedDates = Record<
  string,
  {
    totalActivities: number
  }
>

const dateKeyFormat = 'dd/MM/yyyy HH'

export const toFormattedDateKey = (date: Date): string => formatDate(date, dateKeyFormat)

export const transformToKeyedDate = (
  transformedTotalActivitiesData: TransformedDateTotalActivitiesData,
): KeyedDates => ({
  [toFormattedDateKey(transformedTotalActivitiesData.date)]: {
    totalActivities: transformedTotalActivitiesData.totalActivities,
  },
})

type GetEmptyKeyedDatesForDateRangeArgs = {
  startDate: Date
  endDate: Date
  timeInterval: TimeInterval
}

export const getEmptyKeyedDatesForDateRange = ({
  startDate,
  endDate,
  timeInterval,
}: GetEmptyKeyedDatesForDateRangeArgs): KeyedDates =>
  getDatesBetweenRange({
    startDate,
    endDate,
    timeInterval,
  }).reduce(
    (keyedDates, date) => ({
      [toFormattedDateKey(date)]: { totalActivities: 0 },
      ...keyedDates,
    }),
    {},
  )

type AddEmptyDatestoTotalActivitiesDataArgs = {
  data?: TransformedDateTotalActivitiesData[]
  startDate: Date
  endDate: Date
  timeInterval: TimeInterval
}

export const addEmptyDatestoTotalActivitiesData = ({
  data = [],
  startDate,
  endDate,
  timeInterval,
}: AddEmptyDatestoTotalActivitiesDataArgs): TransformedDateTotalActivitiesData[] => {
  const emptyKeyedDates: KeyedDates = getEmptyKeyedDatesForDateRange({
    startDate,
    endDate,
    timeInterval,
  })

  const populatedKeyedDates: KeyedDates = data.reduce(
    (keyedDates, currentData) => ({
      ...keyedDates,
      ...transformToKeyedDate(currentData),
    }),
    emptyKeyedDates,
  )

  return Object.keys(populatedKeyedDates).map(key => ({
    date: parse(key, dateKeyFormat, new Date()),
    totalActivities: populatedKeyedDates[key].totalActivities,
  }))
}

export const getCsvFileName = (email: string, startDate: Date, endDate: Date) =>
  `${email}-activity-${formatDate(startDate, yyyyMMddHHmm)}-${formatDate(
    endDate,
    yyyyMMddHHmm,
  )}.csv`

type GetActivitiesForUserCsvUrlArgs = {
  customerId: string
  userId: string
  email: string
  startDate: Date
  endDate: Date
  token: string
  accessKey: string
}

export const getActivitiesForUserCsv = async ({
  startDate,
  endDate,
  userId,
  email,
  customerId,
  token,
  accessKey,
}: GetActivitiesForUserCsvUrlArgs): Promise<void> => {
  const agentService = createAgentApiService(token, accessKey)

  const blob = await agentService.getActivitiesForUserCsv({
    startDate,
    endDate,
    userId,
    customerId,
  })

  downloadBlob(getCsvFileName(email, startDate, endDate), blob)

  return Promise.resolve()
}

export const toDateTotalsGraphDataPoint = (
  data: TransformedDateTotalActivitiesData,
): DataPoint => ({
  ...data,
  total: data.totalActivities,
})
